// RD-PERF-003 — Recent-activity query for the admin/billing dashboards.
// ONE tenant-scoped, server-filtered, limit-N read (the old path pulled 40 raw
// rows by entity type and filtered client-side). Which events show is unchanged:
// the pairs come from the single source of truth, dashboardActivityAllowlist().
// The tenant filter only NARROWS — RLS stays the boundary.
import { supabase } from '../supabase';
import {
  dashboardActivityAllowlist,
  filterDashboardAuditEvents,
} from '../utils/activity/dashboardRecentActivity';

const TOKEN_RE = /^[a-z_]+$/;

/** `entity:event` allowlist keys → PostgREST `or=` pair string. Exported for tests. */
export function buildActivityPairs(profile) {
  const byEntity = new Map();
  for (const key of dashboardActivityAllowlist(profile)) {
    const [entity, event] = String(key).split(':');
    if (!TOKEN_RE.test(entity || '') || !TOKEN_RE.test(event || '')) {
      if (process.env.NODE_ENV === 'development') {
        throw new Error(`dashboardActivity: unsafe allowlist token "${key}"`);
      }
      continue;
    }
    if (!byEntity.has(entity)) byEntity.set(entity, []);
    byEntity.get(entity).push(event);
  }
  return [...byEntity.entries()]
    .map(([entity, events]) => `and(entity_type.eq.${entity},event_type.in.(${events.join(',')}))`)
    .join(',');
}

/**
 * @param {{ tenantId: string|null, profile: 'admin'|'billing', limit?: number }} p
 * @returns {Promise<{ data: object[], error: any }>}
 */
export async function fetchDashboardActivity({ tenantId, profile, limit = 8 }) {
  if (!tenantId) return { data: [], error: null }; // fail closed — never query un-scoped
  const pairs = buildActivityPairs(profile);
  if (!pairs) return { data: [], error: null };
  const { data, error } = await supabase
    .from('audit_events')
    .select('id, tenant_id, entity_type, entity_id, event_type, actor_id, actor_name, actor_role, payload, created_at')
    .eq('tenant_id', tenantId)
    .or(pairs)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    // bounded: dashboard widget shows `limit` rows
    .limit(limit);
  if (error) return { data: [], error };
  // Defense in depth: same client allowlist the widget always applied.
  return { data: filterDashboardAuditEvents(data, profile, limit), error: null };
}

export default fetchDashboardActivity;
